import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Checkbox,
    FormControlLabel,
    Grid,
    Typography,
} from '@mui/material';
import { GridExpandMoreIcon } from '@mui/x-data-grid';
import { LocalizationProvider, TimePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useTranslation } from 'react-i18next';
import { updateObjectInArray } from 'utils/array-helpers';
import { Place } from '../types/dtos';
import { HandleChangeType } from 'hooks/useForm';

const TimeInput = ({ value, onChange, disabled, label }) => {
    const [hours, minutes] = value.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);

    const handleTimeChange = (newValue: Date | null) => {
        if (newValue) {
            const updatedTime = `${newValue.getHours().toString().padStart(2, '0')}:${newValue.getMinutes().toString().padStart(2, '0')}`;
            onChange(updatedTime);
        } else {
            onChange('');
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <TimePicker
                label={label}
                value={date}
                onChange={handleTimeChange}
                ampm={false} // 24-hour format
                disabled={disabled}
            />
        </LocalizationProvider>
    );
};

type WorkingHoursProps = {
    place: Place;
    onChange: HandleChangeType<Place>;
};

export const WorkingHoursAccordion = ({ place, onChange }: WorkingHoursProps) => {
    const { t } = useTranslation();

    return (
        <Accordion>
            <AccordionSummary expandIcon={<GridExpandMoreIcon />} role="button">
                <Typography>{t('PLACE_FORM.LABEL_WORKING_HOURS')}</Typography>
            </AccordionSummary>
            <AccordionDetails>
                <Grid container spacing={2}>
                    {place.workingHours.map((workingDay) => (
                        <Grid item xs={6} key={workingDay.dayOfWeek}>
                            <Grid container alignItems="center" spacing={1}>
                                <Grid item>
                                    <Typography variant="body1" gutterBottom style={{ marginBottom: 0 }}>
                                        {t(`PLACE_FORM.LABEL_DAY_${workingDay.dayOfWeek.toUpperCase()}`)}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <FormControlLabel
                                        label={t('PLACE_FORM.LABEL_CLOSED')}
                                        control={
                                            <Checkbox
                                                checked={workingDay.isClosed}
                                                onChange={(e) =>
                                                    onChange(
                                                        'workingHours',
                                                        updateObjectInArray(
                                                            place.workingHours,
                                                            'dayOfWeek',
                                                            workingDay.dayOfWeek,
                                                            'isClosed',
                                                            e.target.checked,
                                                        ),
                                                    )
                                                }
                                                color="primary"
                                            />
                                        }
                                        style={{ margin: 0 }}
                                    />
                                </Grid>
                            </Grid>
                            <TimeInput
                                label={t(`PLACE_FORM.LABEL_OPENING_TIME`)}
                                disabled={workingDay.isClosed}
                                value={workingDay.openingTime}
                                onChange={(updatedTime: string) => {
                                    const updatedWorkingHours = updateObjectInArray(
                                        place.workingHours,
                                        'dayOfWeek',
                                        workingDay.dayOfWeek,
                                        'openingTime',
                                        updatedTime,
                                    );
                                    onChange('workingHours', updatedWorkingHours);
                                }}
                            />
                            <TimeInput
                                label={t(`PLACE_FORM.LABEL_CLOSING_TIME`)}
                                value={workingDay.closingTime}
                                disabled={workingDay.isClosed}
                                onChange={(updatedTime: string) =>
                                    onChange(
                                        'workingHours',
                                        updateObjectInArray(
                                            place.workingHours,
                                            'dayOfWeek',
                                            workingDay.dayOfWeek,
                                            'closingTime',
                                            updatedTime,
                                        ),
                                    )
                                }
                            />
                        </Grid>
                    ))}
                </Grid>
            </AccordionDetails>
        </Accordion>
    );
};
